/**
 * @fileoverview Environment URLs, device constants, and auth. OAuth uses REACT_APP_OAUTH_CLIENT (one value per build).
 * Use getEnvForHost/getAuthForHost(host) in tests to avoid mocking document.
 */
export const DEVICES = {
	MOBILE: 'mobile',
	DESKTOP: 'desktop'
};

/** Base URLs per environment (PROD, STAG, UAT, OAUTH_QA, ITE, DEV, LOCAL). */
export const URL = {
	OAUTH_QA: 'http://qa.accounts-internal-stg-cloud.paytm.com',
	STAG: 'https://accounts-staging.paytm.in',
	PROD: 'https://accounts.paytm.com',
	ITE: 'https://oauth-qa.paytm.in',
	UAT: 'https://accounts-uat.paytm.com',
	DEV: 'http://dev.accounts-internal-stg-cloud.paytm.com',
	LOCAL: 'http://localhost'
};
/**
 * Returns base URL for a given host. Use in tests to avoid document.location mock.
 * @param {string} [host] - document.location.host (e.g. 'accounts.paytm.com')
 * @returns {string} URL.PROD | URL.STAG | URL.OAUTH_QA | URL.UAT | URL.DEV | URL.LOCAL
 */
export function getEnvForHost(host) {
	switch (host) {
		case 'accounts.paytm.com':
			return URL.PROD;
		case 'accounts-staging.paytm.in':
			return URL.STAG;
		case 'qa.accounts-internal-stg-cloud.paytm.com':
			return URL.OAUTH_QA;
		case 'oauth-qa.paytm.in':
			return URL.ITE;
		case 'accounts-uat.paytm.com':
			return URL.UAT;
		case 'dev.accounts-internal-stg-cloud.paytm.com':
			return URL.DEV;
		default:
			return URL.LOCAL;
	}
}

/**
 * Returns base URL for current origin. Reads document.location.host.
 * @returns {string} One of URL.*
 */
export function getCurrentEnv() {
	const host = document && document.location && document.location.host;
	return getEnvForHost(host);
}

/** Default feature flags and config keys (e.g. iosPasswordRule, redirectPaytmDeepLink). */
export const defaultConfig = {
	redirectPaytmDeepLink: 'https://m.paytm.me/login_mob',
	iosPasswordRule:
		'minlength: 8; maxlength: 12; required: lower; required: upper; required: digit; required: [{},/@$#+:.";_-+];', // pragma: allowlist secret
	logoutConsentChecked: false,
	fraud_detection_ivr_number: '1800120130',
	otpFreezeConsent: false,
	forget_password_ivr_number: '1800120130',
	userLogoutConsent: 'LOGOUT_FROM_ALL_DEVICES',
	sdk_config:
		'{"login":{"email":true,"mobile":false,"QR":{"primary":false},"description":""},"signup":{"email":{"required":false},"confirmPass":true,"consent":{"title":"I hearby confirm that I am above 18 years old."},"description":""},"forgetPassword":{"emailReset":true}}',
	country_name_country_code_map:
		'Singapore=65,Hong Kong=852,Cyprus=357,Portugal=351,Oman=968,Russia=7,Malta=356,Saudi Arabia=966,Greece=30,Austria=43,Latvia=371,Netherlands=31,Sweden=46,Republic Of Korea=82,Qatar=974,China=86,Ireland=353,Luxembourg=352,Brazil=55,Poland=48,Slovakia=421,Slovenia=386,France=33,Bulgaria=359,Lithuania=370,Croatia=385,USA=1,Argentina=54,Romania=40,Hungary=36,Japan=81,United Kingdom=44,United Arab Emirates=971,India=91,Spain=34,Canada=1,Czech Republic=420,Turkey=90,Belgium=32,Finland=358,Denmark=45,Italy=39,Mexico=52,South Africa=27,Australia=61,Germany=49,Indonesia=62,Estonia=372',
	email_validation_regex:
		'^[_A-Za-z0-9-\\+]+(\\.[_A-Za-z0-9-]+)*(\\+[_A-Za-z0-9-]+){0,1}@[A-Za-z0-9-]+(\\.[A-Za-z0-9-]+)*(\\.[A-Za-z]{2,})$'
};

/**
 * Accounts / API base origin. Resolution order:
 * 1) `REACT_APP_BASE_URL` (optional alias)
 * 2) `REACT_APP_API_BASE_URL` (e.g. Jenkins: one value per deployment — QA / STAG / PROD / …)
 * 3) else host-based URL from `URL` via getCurrentEnv()
 * @returns {string}
 */
export function getBaseUrl() {
	const fromEnv = process.env.REACT_APP_BASE_URL || process.env.REACT_APP_API_BASE_URL || '';
	if (fromEnv) {
		return fromEnv;
	}
	return getCurrentEnv();
}

let warnedMissingOAuth = false;

/**
 * Returns OAuth Authorization value for this build (e.g. Basic base64). Production pipelines
 * set the prod credential; staging/local/non-prod builds set the non-prod credential — same
 * variable name, different value per deployment.
 * @param {string} [_host] - Unused; kept for callers/tests that pass document.location.host.
 * @returns {string} Token or ''
 */
export function getAuthForHost(_host) {
	const token = process.env.REACT_APP_OAUTH_CLIENT || '';
	if (!token && !warnedMissingOAuth) {
		warnedMissingOAuth = true;
		console.error('REACT_APP_OAUTH_CLIENT environment variable not set');
	}
	return token;
}

/**
 * Returns OAuth Authorization header value for current origin (same as getAuthForHost for this build).
 * @returns {string} Token or ''
 */
export function getAuthorization() {
	const host = document && document.location && document.location.host;
	return getAuthForHost(host);
}
